/**
 * "Today's Status" rollup view-model.
 *
 * Folds the dashboard status foundation's live items into one row per category,
 * each showing the worst band of its items today. It reads the same `/dashboard/
 * status` response as the priority list and summary cards, so the rows reconcile
 * — it only groups and picks the worst band, it never derives one.
 */

import type { DashboardStatusItemDto } from "@/generated/model";
import { DashboardStatusItemDtoBand } from "@/generated/model";
import {
  CATEGORY_FEATURE_MAP,
  type CategoryKey,
  TYPE_META,
} from "@/lib/dashboard/category-map";
import type { DashboardStatus } from "@/lib/dashboard/traffic-light";
import type { FeatureKey } from "@/lib/features";

export interface DomainStatus {
  key: string;
  label: string;
  status: DashboardStatus;
  /** True when this row is a placeholder pending its backend (demo data). */
  placeholder?: boolean;
  /**
   * True when this category is gated behind a plan-tier feature the org's plan
   * doesn't include. The panel renders it as an "Upgrade" prompt instead of a
   * traffic-light status. `status` is ignored for locked rows.
   */
  locked?: boolean;
}

/** Live categories, in the order the panel renders them. */
const LIVE_CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: "checklists", label: "Checklists" },
  { key: "monitoring", label: "Monitoring" },
  { key: "compliance", label: "Compliance" },
  { key: "follow-ups", label: "Follow-ups" },
  { key: "events", label: "Events" },
];

/**
 * Coming-soon categories: rendered as placeholders with no live band.
 *
 * "Incidents" and "Complaints" used to sit here. They were placeholders for event
 * records specifically — incident reports and parent complaints are two of the
 * three seeded event types — so NAU-370 replaced both with the single live Events
 * row above rather than leaving them inert beside it. Risk Assessments has no
 * feature behind it yet and stays a placeholder.
 */
const COMING_SOON: { key: string; label: string }[] = [
  { key: "risk-assessments", label: "Risk Assessments" },
];

/** Worst-first: higher rank wins when folding a category's items. */
const BAND_RANK: Record<DashboardStatus, number> = {
  ON_TRACK: 0,
  NEEDS_ATTENTION: 1,
  ACTION: 2,
};

/** The bands a category row can show; NO_STATUS (sleep/garden) yields null. */
function toBand(band: DashboardStatusItemDto["band"]): DashboardStatus | null {
  if (band === DashboardStatusItemDtoBand.ACTION) return "ACTION";
  if (band === DashboardStatusItemDtoBand.NEEDS_ATTENTION)
    return "NEEDS_ATTENTION";
  if (band === DashboardStatusItemDtoBand.ON_TRACK) return "ON_TRACK";
  return null;
}

export interface TodaysStatusOptions {
  /**
   * The features the org's plan includes (the org's `features` array). A live
   * category mapped to a feature not in this list renders as a locked "Upgrade"
   * row. Pass `undefined` while the org is still resolving so nothing is locked
   * prematurely (avoids a flash of "Upgrade" before the answer is definitive).
   */
  availableFeatures?: FeatureKey[];
}

/**
 * Roll the live status items up to one row per category. Live categories always
 * appear (defaulting to On track when nothing is banded); the coming-soon
 * categories follow as placeholders. Categories gated behind a plan feature the
 * org lacks are marked `locked` so the panel shows an "Upgrade" prompt.
 */
export function mapToTodaysStatus(
  items: DashboardStatusItemDto[],
  { availableFeatures }: TodaysStatusOptions = {},
): DomainStatus[] {
  const worst = new Map<CategoryKey, DashboardStatus>();

  for (const item of items) {
    if (!item.live) continue;
    const meta = TYPE_META[item.type];
    if (!meta) continue;
    const band = toBand(item.band);
    if (!band) continue;
    const current = worst.get(meta.categoryKey);
    if (current === undefined || BAND_RANK[band] > BAND_RANK[current]) {
      worst.set(meta.categoryKey, band);
    }
  }

  // A category is locked when it maps to a feature the org's plan doesn't
  // include. While `availableFeatures` is undefined (org still resolving) we
  // treat everything as unlocked to avoid a flash of the upgrade state.
  const isLocked = (key: CategoryKey): boolean => {
    const feature = CATEGORY_FEATURE_MAP[key];
    if (!feature || availableFeatures === undefined) return false;
    return !availableFeatures.includes(feature);
  };

  const live: DomainStatus[] = LIVE_CATEGORIES.map((c) => ({
    key: c.key,
    label: c.label,
    status: worst.get(c.key) ?? "ON_TRACK",
    locked: isLocked(c.key),
  }));

  const comingSoon: DomainStatus[] = COMING_SOON.map((c) => ({
    key: c.key,
    label: c.label,
    status: "ON_TRACK",
    placeholder: true,
  }));

  return [...live, ...comingSoon];
}
